import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, IconButton, Grid, LinearProgress, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { orange, grey } from "@mui/material/colors";
import { useNavigate, useParams } from "react-router-dom";
import { LOCATION_VISIT_STATUS } from "@/stack-shared/interfaces/model/location-visit.interface";
import { useTodayLocationVisit, useUpdateUsersLocationVisit } from '@/hooks/api/useLocationVisits';
import { useQueryUsers } from '@/hooks/api/useUsers';
import GenericFormModal from '@/components/form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '@/components/form/GenericForm';
import { getFlexNameLabel } from '@/stack-shared/utils/gen-utils';
const DEFAULT_STATUS = LOCATION_VISIT_STATUS.NOT_STARTED;

const LocationVisitOverview: React.FC = () => {
  const { locationId } = useParams();
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();
  const updateUsersLocationVisitMutation = useUpdateUsersLocationVisit();
  const queryUsersMutation = useQueryUsers();
  const [users, setUsers] = useState<any[]>([]);
  const { data: todayVisitData } = useTodayLocationVisit();
  
  const todayVisit = todayVisitData && locationId && Number(todayVisitData.locationId) === Number(locationId) ? todayVisitData : null;
  
  useEffect(() => {
    queryUsersMutation.mutate({
      skip: 0,
      take: 1000,
      search: '',
      orderBy: 'id',
      orderDirection: 'ASC',
    }, {
      onSuccess: (data: any) => {
        setUsers(data?.data || []);
      },
    });
  }, []);

  if (!todayVisit) {
		navigate('/app/location-service');
    return null;
  }

  const formattedDate = new Date(todayVisit.date!).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const userNames = todayVisit.visitUsers
    ?.map((user) => `${user.firstName} ${user.lastName}`)
    .join(", ") || "No users";

  const startServiceFormGroup = {
    groupName: 'Start site visit',
    fields: [
      { type: GENERIC_FORM_FIELD_TYPE.MULTI_SELECT, label: 'Technicians Present', name: 'users', options: users.map((usr) => { return { label: getFlexNameLabel(usr), value: usr.id?.toString() || "" } }), columns: 12 },
    ]
  }

  return (
    <Card variant="outlined" sx={{ p: 2, position: "relative" }}>
      {/* Subtle Edit Button - Top Right */}
      <GenericFormModal
        visible={showEditModal}
        onDismiss={() => setShowEditModal(false)}
        onSubmit={(values) => {
          updateUsersLocationVisitMutation.mutate({ locationVisitId: todayVisit.id, userIds: values.users }, {
            onSuccess: () => {
              setShowEditModal(false);
            },
            onError: (error: any) => {
              console.error('Failed to update location visit users:', error);
            },
          });
        }}
        initialValues={{ users: todayVisit.visitUsers.map((user) => user.id?.toString()) }}
        fieldGroups={[startServiceFormGroup]}
        title="Edit Visit"
      />
      <IconButton
        onClick={() => setShowEditModal(true)}
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, color: grey[600] }}
      >
        <EditIcon fontSize="small" />
      </IconButton>

      <CardContent>
        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
              Overall Progress
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {todayVisit.percentComplete || 0}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={todayVisit.percentComplete || 0} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: grey[200],
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Box>

        <Typography variant="subtitle1">
          <strong>Visit Date:</strong> {formattedDate}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>Users:</strong> {userNames}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LocationVisitOverview;
